import React from 'react';
import {
  Box, Grid, Button, FormControl, InputLabel, Select, MenuItem,
  TextField, Typography, Paper, Stack, CircularProgress, Autocomplete, Chip, Switch,
  FormControlLabel,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PlaylistAddCheckOutlinedIcon from '@mui/icons-material/PlaylistAddCheckOutlined';
import { getRequest, postRequest } from 'Includes/api/apicall';
import { GET_URL, POST_URL } from 'Includes/urls';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';

const FILTER_PAPER_SX = {
  mt: 3,
  p: 2.5,
  borderRadius: 3,
  boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
  border: '1px solid',
  borderColor: 'divider',
};

const TABLE_PAPER_SX = {
  mt: 3,
  p: 1.5,
  borderRadius: 3,
  boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
  border: '1px solid',
  borderColor: 'divider',
  overflow: 'hidden',
};

function formatAmount(v) {
  const n = parseFloat(v || 0);
  return `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

function plusDaysISO(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

function serviceLabelFields(svc) {
  const serviceName = String(svc?.service_name || svc?.catalog_service_name || svc?.service?.name || '').trim();
  const planName = String(svc?.plan_name || svc?.service_plan_name || svc?.name || '').trim();
  const billingName = String(svc?.billing_name || '').trim();
  return { serviceName, planName, billingName };
}

function isIdCardServiceRow(svc) {
  const notes = String(svc?.notes || '');
  if (notes.startsWith('__ID_CARD_CONFIG__')) return true;
  const { serviceName, planName, billingName } = serviceLabelFields(svc);
  const notesLower = notes.toLowerCase();
  const blob = `${serviceName} ${planName} ${billingName}`.toLowerCase();
  return (
    notesLower.includes('auto id card order from agreement') ||
    blob.includes('id card') ||
    blob.includes('idcard')
  );
}

function isBiometricServiceRow(svc) {
  const notes = String(svc?.notes || '');
  if (notes.startsWith('__BIOMETRIC_CONFIG__')) return true;
  const { serviceName, planName, billingName } = serviceLabelFields(svc);
  return `${serviceName} ${planName} ${billingName}`.toLowerCase().includes('biometric');
}

function isDigitalMarketingServiceRow(svc) {
  if (isIdCardServiceRow(svc) || isBiometricServiceRow(svc)) return false;
  const { serviceName, planName } = serviceLabelFields(svc);
  const notes = String(svc?.notes || '').toLowerCase();
  const blob = `${serviceName} ${planName}`.toLowerCase();
  return (
    svc?.digitalservice != null ||
    notes.includes('auto digital order from agreement') ||
    blob.includes('digital marketing') ||
    (blob.includes('digital') && !blob.includes('erp'))
  );
}

function isErpServiceRow(svc) {
  if (isIdCardServiceRow(svc) || isBiometricServiceRow(svc) || isDigitalMarketingServiceRow(svc)) return false;
  const { serviceName, planName, billingName } = serviceLabelFields(svc);
  const notes = String(svc?.notes || '').toLowerCase();
  if (notes.includes('bulk erp order') || notes.includes('auto erp order')) return true;
  const blob = `${serviceName} ${planName} ${billingName}`.toLowerCase();
  return blob.includes('erp') && !blob.includes('newerp') && !blob.includes('new erp');
}

function getServiceFilterLabel(svc) {
  const { serviceName, planName, billingName } = serviceLabelFields(svc);
  if (isIdCardServiceRow(svc)) return 'ID Cards';
  if (isBiometricServiceRow(svc)) {
    try {
      if (String(svc?.notes || '').startsWith('__BIOMETRIC_CONFIG__')) {
        const cfg = JSON.parse(String(svc.notes).slice('__BIOMETRIC_CONFIG__'.length));
        if (cfg?.kind === 'manual') return 'Manual Biometric';
        if (cfg?.kind === 'auto') return 'Auto Biometric';
      }
    } catch (_) { /* ignore */ }
    return serviceName || planName || 'Biometric';
  }
  if (isDigitalMarketingServiceRow(svc)) {
    return serviceName || planName || billingName || 'Digital Service';
  }
  if (isErpServiceRow(svc)) return 'ERP';
  const parts = [serviceName, planName].filter(Boolean);
  if (parts.length === 2 && parts[0].toLowerCase() === parts[1].toLowerCase()) {
    return parts[0];
  }
  if (parts.length) return parts.join(' - ');
  return billingName || 'Other Service';
}

function getConsumedInvoiceBase(svc) {
  const issueItems = Array.isArray(svc?.issue_invoice_items) ? svc.issue_invoice_items : [];
  const invItems = Array.isArray(svc?.invoice_items) ? svc.invoice_items : [];
  const items = issueItems.length ? issueItems : invItems;
  return items.reduce((sum, it) => sum + (parseFloat(it?.total_without_tax || 0) || 0), 0);
}

function getServiceTermLabels(svc) {
  const terms = Array.isArray(svc?.payment_terms) ? svc.payment_terms : [];
  return terms
    .map((term, index) => String(term?.term_name || '').trim() || `Term ${index + 1}`)
    .filter(Boolean);
}

function getTermBaseAmount(svc, term) {
  const orderBase = parseFloat(svc?.order_base_snapshot || 0) || 0;
  const pct = parseFloat(term?.amount_percentage || 0) || 0;
  const fixedAmount = parseFloat(term?.amount_rs || 0) || 0;
  if (pct > 0) return (orderBase * pct) / 100;
  if (fixedAmount > 0) return fixedAmount;
  return 0;
}

function getPendingTermsForService(svc) {
  if (isDigitalMarketingServiceRow(svc)) return [];
  const terms = Array.isArray(svc?.payment_terms)
    ? [...svc.payment_terms].sort((a, b) => (Number(a?.id) || 0) - (Number(b?.id) || 0))
    : [];
  let consumedBase = getConsumedInvoiceBase(svc);
  return terms.map((term, idx) => {
    const termBase = getTermBaseAmount(svc, term);
    const consumedInThisTerm = Math.max(0, Math.min(termBase, consumedBase));
    const pendingBase = Math.max(0, termBase - consumedInThisTerm);
    consumedBase = Math.max(0, consumedBase - termBase);
    return {
      idx,
      term_name: String(term?.term_name || '').trim() || `Term ${idx + 1}`,
      pendingBase: Number(pendingBase.toFixed(2)),
    };
  }).filter((term) => term.pendingBase > 0.001);
}

function normalizeTermName(name) {
  return String(name || '').trim().toLowerCase();
}

function getItemTermChoices(item) {
  if ((item?.term_options || []).length) return item.term_options;
  return (item?.term_labels || []).map((termLabel, idx) => ({
    idx,
    term_name: termLabel,
    pendingBase: null,
  }));
}

function sortTermNames(names) {
  return [...names].sort((a, b) => {
    const na = String(a).match(/(\d+)/);
    const nb = String(b).match(/(\d+)/);
    if (na && nb && Number(na[1]) !== Number(nb[1])) {
      return Number(na[1]) - Number(nb[1]);
    }
    return String(a).localeCompare(String(b));
  });
}

function getSelectionKey(rowId, item) {
  return `${rowId}:${item.order}`;
}

function buildEnrichedPayload(payload) {
  const updatedServices = (payload?.pricing_services || []).map((svc) => {
    const serviceAmount = parseFloat(svc.service_amount || 0) || 0;
    const gstPercent = (isDigitalMarketingServiceRow(svc) || isIdCardServiceRow(svc))
      ? 0
      : (parseFloat(svc.service_gst_percentage || 0) || 0);
    const itemCount = parseFloat(svc.item_count || 1) || 1;
    const originalBase = serviceAmount * itemCount;
    const originalGst = gstPercent > 0 ? (originalBase * (gstPercent / 100)) : 0;
    let invoicedCount = 0;
    let invoicedBaseAmount = 0;
    const issueItems = Array.isArray(svc.issue_invoice_items) ? svc.issue_invoice_items : [];
    const invItems = Array.isArray(svc.invoice_items) ? svc.invoice_items : [];
    const consumedItems = issueItems.length ? issueItems : invItems;
    for (const it of consumedItems) {
      invoicedCount += parseFloat(it.item_count || 0) || 0;
      invoicedBaseAmount += parseFloat(it.total_without_tax || 0) || 0;
    }
    const remainingCount = itemCount - invoicedCount;
    const remainingBaseAmount = Math.max(originalBase - invoicedBaseAmount, 0);
    const hasQtyRemaining = remainingCount > 0.0001;
    const hasAmountRemaining = remainingBaseAmount > 0.01;
    const isAmountBased = isErpServiceRow(svc) || isBiometricServiceRow(svc);
    const isAllInvoiced = isAmountBased
      ? !hasAmountRemaining
      : (!hasQtyRemaining && !hasAmountRemaining);
    const keepOrderMetrics = isAmountBased || (!hasQtyRemaining && hasAmountRemaining);
    const invoiceItemCount = isAllInvoiced
      ? 0
      : (keepOrderMetrics ? itemCount : remainingCount);
    const invoiceUnitAmount = serviceAmount;
    const baseAmount = keepOrderMetrics ? remainingBaseAmount : (invoiceUnitAmount * invoiceItemCount);
    const gstAmount = gstPercent > 0 ? (baseAmount * (gstPercent / 100)) : 0;
    return {
      ...svc,
      item_count: invoiceItemCount,
      service_amount: invoiceUnitAmount,
      order_base_snapshot: originalBase,
      order_gst_snapshot: originalGst,
      order_total_snapshot: originalBase + originalGst,
      remainingBaseAmount,
      service_base: baseAmount,
      service_gst: gstAmount,
      service_total: baseAmount + gstAmount,
      discount_amount: 0,
      orderItemCount: itemCount,
      orderSvcAmount: serviceAmount,
      isAllInvoiced,
    };
  });
  return { ...payload, pricing_services: updatedServices };
}

function buildIssueInvoiceItems(payload) {
  const services = Array.isArray(payload?.pricing_services) ? payload.pricing_services : [];
  return services
    .filter((svc) => !svc.isAllInvoiced)
    .map((svc) => {
      const taxPercent = (isDigitalMarketingServiceRow(svc) || isIdCardServiceRow(svc))
        ? 0
        : (parseFloat(svc.service_gst_percentage || 0) || 0);
      const itemCount = parseFloat(svc.orderItemCount || svc.item_count || 0);
      const amount = parseFloat(svc.orderSvcAmount || svc.service_amount || 0);
      const selectedBase = parseFloat(svc.service_base || 0) || 0;
      const qtyTimesUnit = itemCount * amount;
      const row = {
        order: svc.order || svc.order_id || svc.id,
        item_count: itemCount,
        amount,
        tax_percent: Number.isFinite(taxPercent) ? taxPercent : 0,
        service_gst_percentage: Number.isFinite(taxPercent) ? taxPercent : 0,
        gst_supported: !isDigitalMarketingServiceRow(svc) && !isIdCardServiceRow(svc),
        hsn_sac: svc.hsn_sac || '',
        discount_amount: parseFloat(svc.discount_amount || 0),
        notes: svc.notes || '',
        service_label: getServiceFilterLabel(svc),
        term_labels: getServiceTermLabels(svc),
        term_options: getPendingTermsForService(svc),
      };
      if (selectedBase > 0.01 && Math.abs(qtyTimesUnit - selectedBase) > 0.02) {
        row.line_total_without_tax = selectedBase;
      }
      return row;
    })
    .filter((item) => item.order && item.item_count > 0 && item.amount > 0);
}

function BulkCreateIssueInvoices(props) {
  const navigate = useNavigate();
  const [academicYearList, setAcademicYearList] = React.useState([]);
  const [serviceOptions, setServiceOptions] = React.useState([]);
  const [selectedAcademicYear, setSelectedAcademicYear] = React.useState('');
  const [selectedServicesFilter, setSelectedServicesFilter] = React.useState([]);
  const [issueDate, setIssueDate] = React.useState(todayISO());
  const [dueDate, setDueDate] = React.useState(plusDaysISO(15));
  const [commonNotes, setCommonNotes] = React.useState('');
  const [allRows, setAllRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [selectedIds, setSelectedIds] = React.useState([]);
  const [itemSelections, setItemSelections] = React.useState({});
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(25);
  const [totalCount, setTotalCount] = React.useState(0);
  const [hasNextPage, setHasNextPage] = React.useState(false);
  const [hasPreviousPage, setHasPreviousPage] = React.useState(false);
  const [globalTermNames, setGlobalTermNames] = React.useState(null);
  const ignoreNextEmptySelection = React.useRef(false);
  const globalTermNamesRef = React.useRef(null);

  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const year = params.get('year');
    if (year) setSelectedAcademicYear(year);
    getRequest(GET_URL.academicyear.api, {}, props).then((res) => {
      if (res?.status === 200) {
        setAcademicYearList(res.data?.data || []);
      }
    });
    Promise.allSettled([
      getRequest(GET_URL.plans.api, {}, props),
      getRequest(GET_URL.digitalmarketingfeature.api, {}, props),
    ]).then(([plansRes, digitalRes]) => {
      const planList = plansRes?.status === 'fulfilled' && plansRes.value?.status === 200
        ? (plansRes.value.data?.data || plansRes.value.data || [])
        : [];
      const digitalList = digitalRes?.status === 'fulfilled' && digitalRes.value?.status === 200
        ? (digitalRes.value.data?.data || digitalRes.value.data || [])
        : [];
      const labels = new Set();
      (Array.isArray(planList) ? planList : []).forEach((plan) => {
        const label = getServiceFilterLabel(plan);
        if (label) labels.add(label);
      });
      (Array.isArray(digitalList) ? digitalList : []).forEach((item) => {
        const label = String(item?.name || '').trim();
        if (label) labels.add(label);
      });
      setServiceOptions(Array.from(labels).sort((a, b) => a.localeCompare(b)));
    });
  }, []);

  const loadCandidates = React.useCallback(async (year, selectedServices, nextPage, nextPageSize) => {
    if (!year || !Array.isArray(selectedServices) || !selectedServices.length) {
      setAllRows([]);
      setSelectedIds([]);
      setTotalCount(0);
      setHasNextPage(false);
      setHasPreviousPage(false);
      return;
    }
    setLoading(true);
    try {
      const orders = [];
      let orderPage = 1;
      const orderPageSize = 1000;
      while (orderPage <= 50) {
        const orderRes = await getRequest(
          GET_URL.service_order.api,
          { year, limit: orderPageSize, pageno: orderPage },
          props
        );
        if (orderRes?.status !== 200) break;
        const batch = Array.isArray(orderRes.data?.data) ? orderRes.data.data : [];
        orders.push(...batch);
        if (!orderRes.data?.next || !batch.length) break;
        orderPage += 1;
      }
      const matchingConfigs = [];
      const seenConfigIds = new Set();
      orders.forEach((order) => {
        const configId = order.billing_config;
        if (!configId || seenConfigIds.has(configId)) return;
        const label = getServiceFilterLabel({
          service_name: order.catalog_service_name || order.service_name,
          plan_name: order.service_plan_name,
          notes: order.notes,
          digitalservice: order.digitalservice,
        });
        if (!selectedServices.includes(label)) return;
        seenConfigIds.add(configId);
        matchingConfigs.push({
          id: configId,
          company: order.company,
          company_name: order.company_name,
        });
      });
      const total = matchingConfigs.length;
      const start = Math.max(0, (Number(nextPage) - 1) * Number(nextPageSize));
      const configs = matchingConfigs.slice(start, start + Number(nextPageSize));
      setTotalCount(total);
      setHasNextPage(start + Number(nextPageSize) < total);
      setHasPreviousPage(Number(nextPage) > 1);
      if (!configs.length) {
        setAllRows([]);
        setSelectedIds([]);
        setItemSelections({});
        return;
      }
      const detailResponses = await Promise.all(
        configs.map((cfg) => getRequest(`${GET_URL.billing_config.api}${cfg.id}/`, {}, props))
      );
      const nextRows = detailResponses.map((res, idx) => {
        const cfg = configs[idx];
        const payload = res?.data?.data || res?.data || {};
        const enriched = buildEnrichedPayload(payload);
        const items = buildIssueInvoiceItems(enriched).filter((item) => item.order);
        const summary = items.reduce((acc, item) => {
          const base = (parseFloat(item.amount || 0) || 0) * (parseFloat(item.item_count || 0) || 0);
          const gst = base * ((parseFloat(item.tax_percent || 0) || 0) / 100);
          acc.base += base;
          acc.gst += gst;
          acc.total += base + gst;
          return acc;
        }, { base: 0, gst: 0, total: 0 });
        return {
          id: cfg.id,
          company: payload.company || cfg.company,
          company_name: payload.company_name || cfg.company_name,
          invoiceable_count: items.length,
          base_amount: summary.base,
          gst_amount: summary.gst,
          total_amount: summary.total,
          items,
        };
      }).filter((row) => row.invoiceable_count > 0).map((row) => ({
        ...row,
        items: row.items.filter((item) => selectedServices.includes(item.service_label)),
      })).map((row) => {
        const filteredItems = row.items.filter(Boolean);
        const summary = filteredItems.reduce((acc, item) => {
          const base = (parseFloat(item.amount || 0) || 0) * (parseFloat(item.item_count || 0) || 0);
          const gst = base * ((parseFloat(item.tax_percent || 0) || 0) / 100);
          acc.base += base;
          acc.gst += gst;
          acc.total += base + gst;
          return acc;
        }, { base: 0, gst: 0, total: 0 });
        return {
          ...row,
          items: filteredItems,
          invoiceable_count: filteredItems.length,
          base_amount: summary.base,
          gst_amount: summary.gst,
          total_amount: summary.total,
        };
      }).filter((row) => row.invoiceable_count > 0);
      const selectedTermSet = Array.isArray(globalTermNamesRef.current)
        ? new Set(globalTermNamesRef.current.map(normalizeTermName))
        : null;
      const defaultSelections = {};
      nextRows.forEach((row) => {
        (row.items || []).forEach((item) => {
          defaultSelections[getSelectionKey(row.id, item)] = {
            gstEnabled: item.gst_supported ? true : false,
            selectedTerms: getItemTermChoices(item).reduce((acc, term) => {
              acc[term.idx] = selectedTermSet
                ? selectedTermSet.has(normalizeTermName(term.term_name))
                : true;
              return acc;
            }, {}),
          };
        });
      });
      ignoreNextEmptySelection.current = true;
      setAllRows(nextRows);
      setSelectedIds(nextRows.map((row) => row.id));
      setItemSelections(defaultSelections);
    } finally {
      setLoading(false);
    }
  }, [props]);

  React.useEffect(() => {
    loadCandidates(selectedAcademicYear, selectedServicesFilter, page, pageSize);
  }, [selectedAcademicYear, selectedServicesFilter, page, pageSize, loadCandidates]);

  React.useEffect(() => {
    setPage(1);
  }, [selectedAcademicYear, selectedServicesFilter, pageSize]);

  React.useEffect(() => {
    globalTermNamesRef.current = null;
    setGlobalTermNames(null);
  }, [selectedAcademicYear, selectedServicesFilter]);

  const availableTermOptions = React.useMemo(() => {
    const names = new Set();
    allRows.forEach((row) => {
      (row.items || []).forEach((item) => {
        getItemTermChoices(item).forEach((term) => {
          const name = String(term?.term_name || '').trim();
          if (name) names.add(name);
        });
      });
    });
    return sortTermNames(Array.from(names));
  }, [allRows]);

  React.useEffect(() => {
    if (!availableTermOptions.length) return;
    setGlobalTermNames((prev) => {
      if (prev == null) {
        globalTermNamesRef.current = availableTermOptions;
        return availableTermOptions;
      }
      const allowed = new Set(availableTermOptions.map(normalizeTermName));
      const next = sortTermNames(prev.filter((name) => allowed.has(normalizeTermName(name))));
      globalTermNamesRef.current = next;
      return next;
    });
  }, [availableTermOptions]);

  const applyGlobalTerms = React.useCallback((termNames) => {
    const nextNames = Array.isArray(termNames) ? termNames : [];
    globalTermNamesRef.current = nextNames;
    setGlobalTermNames(nextNames);
    const selectedSet = new Set(nextNames.map(normalizeTermName));
    setItemSelections((prev) => {
      const next = { ...prev };
      allRows.forEach((row) => {
        (row.items || []).forEach((item) => {
          const selectionKey = getSelectionKey(row.id, item);
          next[selectionKey] = {
            ...(next[selectionKey] || {}),
            selectedTerms: getItemTermChoices(item).reduce((acc, term) => {
              acc[term.idx] = selectedSet.has(normalizeTermName(term.term_name));
              return acc;
            }, {}),
          };
        });
      });
      return next;
    });
  }, [allRows]);

  const updateItemTermSelection = React.useCallback((selectionKey, termChoices, selectedIdxValues) => {
    const selectedSet = new Set((selectedIdxValues || []).map(String));
    setItemSelections((prev) => ({
      ...prev,
      [selectionKey]: {
        ...(prev[selectionKey] || {}),
        selectedTerms: termChoices.reduce((acc, term) => {
          acc[term.idx] = selectedSet.has(String(term.idx));
          return acc;
        }, {}),
      },
    }));
  }, []);

  const applyItemSelections = React.useCallback((rowId, item) => {
    const selection = itemSelections[getSelectionKey(rowId, item)] || {};
    const selectedTerms = selection.selectedTerms || {};
    const selectedTermOptions = (item.term_options || []).filter((term) => selectedTerms[term.idx] !== false);
    if ((item.term_options || []).length > 0 && selectedTermOptions.length === 0) {
      return null;
    }
    const selectedBase = selectedTermOptions.length
      ? selectedTermOptions.reduce((sum, term) => sum + (parseFloat(term.pendingBase || 0) || 0), 0)
      : ((parseFloat(item.amount || 0) || 0) * (parseFloat(item.item_count || 0) || 0));
    if (selectedBase <= 0) return null;
    const nextItem = {
      ...item,
      tax_percent: item.gst_supported && selection.gstEnabled === false
        ? 0
        : (parseFloat(item.service_gst_percentage || 0) || 0),
    };
    if ((item.term_options || []).length > 0) {
      nextItem.item_count = 1;
      nextItem.amount = Number(selectedBase.toFixed(2));
      nextItem.term_labels = selectedTermOptions.map((term) => term.term_name);
    }
    return nextItem;
  }, [itemSelections]);

  const rows = React.useMemo(() => {
    return allRows
      .map((row) => {
        const filteredItems = (row.items || [])
          .map((item) => applyItemSelections(row.id, item))
          .filter(Boolean);
        if (!filteredItems.length) return null;
        const summary = filteredItems.reduce((acc, item) => {
          const base = (parseFloat(item.amount || 0) || 0) * (parseFloat(item.item_count || 0) || 0);
          const gst = base * ((parseFloat(item.tax_percent || 0) || 0) / 100);
          acc.base += base;
          acc.gst += gst;
          acc.total += base + gst;
          return acc;
        }, { base: 0, gst: 0, total: 0 });
        return {
          ...row,
          items: filteredItems,
          invoiceable_count: filteredItems.length,
          base_amount: summary.base,
          gst_amount: summary.gst,
          total_amount: summary.total,
        };
      })
      .filter(Boolean);
  }, [allRows, applyItemSelections]);

  const displayRows = rows;

  React.useEffect(() => {
    const visibleIds = displayRows.map((row) => row.id);
    setSelectedIds((prev) => {
      const nextSelected = prev.filter((id) => visibleIds.includes(id));
      return nextSelected.length ? nextSelected : visibleIds;
    });
  }, [displayRows]);

  const selectedSummary = React.useMemo(() => {
    return displayRows.reduce((acc, row) => {
      if (!selectedIds.includes(row.id)) return acc;
      acc.count += 1;
      acc.base += parseFloat(row.base_amount || 0) || 0;
      acc.gst += parseFloat(row.gst_amount || 0) || 0;
      acc.total += parseFloat(row.total_amount || 0) || 0;
      return acc;
    }, { count: 0, base: 0, gst: 0, total: 0 });
  }, [displayRows, selectedIds]);

  const columns = [
    {
      field: 'company_name',
      headerName: 'Company',
      flex: 1.2,
      minWidth: 220,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight={600} noWrap title={params.value}>
          {params.value || '-'}
        </Typography>
      ),
    },
    {
      field: 'invoiceable_count',
      headerName: 'Services',
      width: 110,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'service_summary',
      headerName: 'Selected services',
      flex: 1,
      minWidth: 220,
      sortable: false,
      renderCell: (params) => {
        const labels = Array.from(new Set((params.row.items || []).map((item) => item.service_label).filter(Boolean)));
        return (
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ py: 1 }}>
            {labels.length ? labels.map((label) => (
              <Chip key={label} label={label} size="small" variant="outlined" />
            )) : <Typography variant="body2" color="text.secondary">-</Typography>}
          </Stack>
        );
      },
    },
    {
      field: 'terms_summary',
      headerName: 'Terms',
      flex: 1.4,
      minWidth: 320,
      sortable: false,
      renderCell: (params) => {
        const originalRow = allRows.find((row) => row.id === params.row.id) || params.row;
        return (
          <Stack spacing={1} sx={{ py: 1, width: '100%' }}>
            {(originalRow.items || [])
              .filter((item) => !selectedServicesFilter.length || selectedServicesFilter.includes(item.service_label))
              .map((item) => {
                const selectionKey = getSelectionKey(params.row.id, item);
                const selection = itemSelections[selectionKey] || {};
                const selectedTerms = selection.selectedTerms || {};
                const termChoices = getItemTermChoices(item);
                return (
                  <Box
                    key={`${selectionKey}-terms`}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1 }}
                  >
                    <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.75 }}>
                      {item.service_label}
                    </Typography>
                    {termChoices.length > 0 ? (
                      <Box>
                        <FormControl size="small" fullWidth>
                          <InputLabel>Terms</InputLabel>
                          <Select
                            multiple
                            label="Terms"
                            value={termChoices
                              .filter((term) => selectedTerms[term.idx] !== false)
                              .map((term) => String(term.idx))}
                            onChange={(e) => {
                              const selectedValues = Array.isArray(e.target.value) ? e.target.value : [];
                              updateItemTermSelection(selectionKey, termChoices, selectedValues);
                            }}
                            renderValue={(selected) => {
                              const selectedSet = new Set((selected || []).map(String));
                              const labels = termChoices
                                .filter((term) => selectedSet.has(String(term.idx)))
                                .map((term) => term.term_name);
                              return labels.join(', ');
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            {termChoices.map((term) => (
                              <MenuItem key={`${selectionKey}-term-${term.idx}`} value={String(term.idx)}>
                                {term.term_name}
                                {term.pendingBase != null ? ` (${formatAmount(term.pendingBase)})` : ''}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
                          {termChoices
                            .filter((term) => selectedTerms[term.idx] !== false)
                            .map((term) => (
                              <Chip
                                key={`${selectionKey}-selected-${term.idx}`}
                                label={term.pendingBase != null ? `${term.term_name} (${formatAmount(term.pendingBase)})` : term.term_name}
                                size="small"
                                color="secondary"
                                variant="outlined"
                              />
                            ))}
                        </Stack>
                      </Box>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        No terms
                      </Typography>
                    )}
                  </Box>
                );
              })}
          </Stack>
        );
      },
    },
    {
      field: 'gst_controls',
      headerName: 'GST',
      width: 180,
      sortable: false,
      renderCell: (params) => {
        const originalRow = allRows.find((row) => row.id === params.row.id) || params.row;
        return (
          <Stack spacing={1} sx={{ py: 1, width: '100%' }}>
            {(originalRow.items || [])
              .filter((item) => !selectedServicesFilter.length || selectedServicesFilter.includes(item.service_label))
              .map((item) => {
                const selectionKey = getSelectionKey(params.row.id, item);
                const selection = itemSelections[selectionKey] || {};
                return (
                  <Box
                    key={`${selectionKey}-gst`}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1 }}
                  >
                    <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
                      {item.service_label}
                    </Typography>
                    {item.gst_supported ? (
                      <FormControlLabel
                        sx={{ m: 0, mt: 0.5 }}
                        control={(
                          <Switch
                            size="small"
                            checked={selection.gstEnabled !== false}
                            onChange={(e) => {
                              setItemSelections((prev) => ({
                                ...prev,
                                [selectionKey]: { ...(prev[selectionKey] || {}), gstEnabled: e.target.checked },
                              }));
                            }}
                          />
                        )}
                        label={<Typography variant="caption">Enabled</Typography>}
                      />
                    ) : (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                        Not applicable
                      </Typography>
                    )}
                  </Box>
                );
              })}
          </Stack>
        );
      },
    },
    {
      field: 'base_amount',
      headerName: 'Base',
      width: 130,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => formatAmount(params.value),
    },
    {
      field: 'gst_amount',
      headerName: 'GST',
      width: 130,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => formatAmount(params.value),
    },
    {
      field: 'total_amount',
      headerName: 'Total',
      width: 140,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => (
        <Typography variant="body2" fontWeight={700} sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {formatAmount(params.value)}
        </Typography>
      ),
    },
  ];

  const handleSubmit = async () => {
    if (!selectedAcademicYear) {
      Swal.fire('Select year', 'Please select an academic year first', 'warning');
      return;
    }
    if (!issueDate || !dueDate) {
      Swal.fire('Dates required', 'Please enter issue date and due date', 'warning');
      return;
    }
    if (new Date(dueDate) < new Date(issueDate)) {
      Swal.fire('Invalid dates', 'Due date should be greater than or equal to issue date', 'warning');
      return;
    }
    const targetRows = displayRows.filter((row) => selectedIds.includes(row.id) && row.items?.length);
    if (!targetRows.length) {
      Swal.fire('Select companies', 'Select at least one company with invoiceable services', 'warning');
      return;
    }
    const summary = targetRows.reduce((acc, row) => {
      acc.companyCount += 1;
      acc.serviceCount += (row.items || []).length;
      acc.base += parseFloat(row.base_amount || 0) || 0;
      acc.gst += parseFloat(row.gst_amount || 0) || 0;
      acc.total += parseFloat(row.total_amount || 0) || 0;
      return acc;
    }, {
      companyCount: 0, serviceCount: 0, base: 0, gst: 0, total: 0,
    });
    const companySummaryRows = targetRows
      .map((row) => `
        <tr>
          <td style="padding:6px 8px; border-bottom:1px solid #eee; text-align:left;">${row.company_name}</td>
          <td style="padding:6px 8px; border-bottom:1px solid #eee; text-align:center;">${(row.items || []).length}</td>
          <td style="padding:6px 8px; border-bottom:1px solid #eee; text-align:right;">${formatAmount(row.base_amount)}</td>
          <td style="padding:6px 8px; border-bottom:1px solid #eee; text-align:right;">${formatAmount(row.gst_amount)}</td>
          <td style="padding:6px 8px; border-bottom:1px solid #eee; text-align:right; font-weight:700;">${formatAmount(row.total_amount)}</td>
        </tr>
      `)
      .join('');
    const result = await Swal.fire({
      title: 'Create bulk issue invoices?',
      width: 1100,
      html: `
        <div style="text-align:left;">
          <p style="margin:0 0 8px 0;">Please confirm the bulk invoice summary:</p>
          <ul style="margin:0 0 12px 18px; padding:0;">
            <li><b>Companies:</b> ${summary.companyCount}</li>
            <li><b>Services:</b> ${summary.serviceCount}</li>
            <li><b>Base:</b> ${formatAmount(summary.base)}</li>
            <li><b>GST:</b> ${formatAmount(summary.gst)}</li>
            <li><b>Total:</b> ${formatAmount(summary.total)}</li>
          </ul>
          <p style="margin:0 0 6px 0;"><b>Company-wise summary</b></p>
          <div style="max-height:320px; overflow:auto; border:1px solid #eee; border-radius:6px;">
            <table style="width:100%; border-collapse:collapse; font-size:13px;">
              <thead style="position:sticky; top:0; background:#fafafa;">
                <tr>
                  <th style="padding:8px; text-align:left; border-bottom:1px solid #ddd;">Company</th>
                  <th style="padding:8px; text-align:center; border-bottom:1px solid #ddd;">Services</th>
                  <th style="padding:8px; text-align:right; border-bottom:1px solid #ddd;">Base</th>
                  <th style="padding:8px; text-align:right; border-bottom:1px solid #ddd;">GST</th>
                  <th style="padding:8px; text-align:right; border-bottom:1px solid #ddd;">Total</th>
                </tr>
              </thead>
              <tbody>
                ${companySummaryRows}
              </tbody>
            </table>
          </div>
        </div>
      `,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Create',
    });
    if (!result.isConfirmed) return;

    setSubmitting(true);
    const createdCompanies = [];
    const failedCompanies = [];
    try {
      for (const row of targetRows) {
        const response = await postRequest(
          POST_URL.issued_invoices.api,
          {
            billing_config: row.id,
            issue_date: issueDate,
            due_date: dueDate,
            status: 'DRAFT',
            notes: commonNotes || '',
            items: row.items,
          },
          props
        );
        if (response?.status === 200) {
          createdCompanies.push(row.company_name);
        } else {
          failedCompanies.push(row.company_name);
        }
      }
      const message = failedCompanies.length
        ? `Created ${createdCompanies.length} invoice(s). Failed: ${failedCompanies.join(', ')}`
        : `Created ${createdCompanies.length} issue invoice(s).`;
      await Swal.fire(
        failedCompanies.length ? 'Completed with errors' : 'Done',
        message,
        failedCompanies.length ? 'warning' : 'success'
      );
      loadCandidates(selectedAcademicYear, selectedServicesFilter, page, pageSize);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Grid container alignItems="center" spacing={2}>
        <Grid item xs={12} md={7}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/billing/issue-invoice/list')}
              sx={{ textTransform: 'none' }}
            >
              Back
            </Button>
            <Box>
              <Typography variant="h5" fontWeight={600}>
                Create Bulk Issue Invoices
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Create draft issue invoices for companies with created orders using their remaining invoiceable services.
              </Typography>
            </Box>
          </Stack>
        </Grid>
        <Grid item xs={12} md={5} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
          <Button
            variant="contained"
            startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <PlaylistAddCheckOutlinedIcon />}
            disabled={submitting || !selectedIds.length}
            onClick={handleSubmit}
            sx={{ borderRadius: 2, textTransform: 'none', px: 2.5 }}
          >
            Create Invoices ({selectedSummary.count})
          </Button>
        </Grid>
      </Grid>

      <Paper sx={FILTER_PAPER_SX}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Academic Year</InputLabel>
              <Select
                value={selectedAcademicYear}
                label="Academic Year"
                onChange={(e) => setSelectedAcademicYear(e.target.value)}
              >
                {academicYearList.map((y) => (
                  <MenuItem key={y.id} value={y.id}>{y.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Autocomplete
              multiple
              options={serviceOptions}
              value={selectedServicesFilter}
              onChange={(_e, value) => setSelectedServicesFilter(value)}
              disableCloseOnSelect
              renderTags={(value, getTagProps) => value.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option}
                  label={option}
                  size="small"
                />
              ))}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  size="small"
                  label="Services"
                  placeholder={selectedServicesFilter.length ? '' : 'Select service'}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Autocomplete
              multiple
              options={availableTermOptions}
              value={globalTermNames || []}
              onChange={(_e, value) => applyGlobalTerms(value)}
              disableCloseOnSelect
              disabled={!selectedServicesFilter.length || !availableTermOptions.length}
              renderTags={(value, getTagProps) => value.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option}
                  label={option}
                  size="small"
                  color="secondary"
                />
              ))}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  size="small"
                  label="Terms"
                  placeholder={
                    !selectedServicesFilter.length
                      ? 'Select service first'
                      : availableTermOptions.length
                        ? ''
                        : 'No terms found'
                  }
                  helperText={availableTermOptions.length ? 'Applies to all companies (override per row below)' : ' '}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Issue date"
              InputLabelProps={{ shrink: true }}
              value={issueDate}
              onChange={(e) => setIssueDate(e.target.value)}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Due date"
              InputLabelProps={{ shrink: true }}
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
            />
          </Grid>

          <Grid item xs={12} md={2}>
            <Button
              variant="outlined"
              onClick={() => loadCandidates(selectedAcademicYear, selectedServicesFilter, page, pageSize)}
              disabled={!selectedAcademicYear || !selectedServicesFilter.length || loading}
              sx={{ textTransform: 'none', width: '100%' }}
            >
              Refresh
            </Button>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="Common notes"
              value={commonNotes}
              onChange={(e) => setCommonNotes(e.target.value)}
            />
          </Grid>
        </Grid>
      </Paper>

      {selectedAcademicYear && (
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12} sm={4}>
            <Paper sx={{ p: 2, borderRadius: 2, bgcolor: 'action.hover' }}>
              <Typography variant="caption" color="text.secondary">Selected base</Typography>
              <Typography variant="h6" fontWeight={600}>{formatAmount(selectedSummary.base)}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Paper sx={{ p: 2, borderRadius: 2, bgcolor: 'action.hover' }}>
              <Typography variant="caption" color="text.secondary">GST</Typography>
              <Typography variant="h6" fontWeight={600}>{formatAmount(selectedSummary.gst)}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Paper sx={{ p: 2, borderRadius: 2, bgcolor: 'primary.main', color: 'primary.contrastText' }}>
              <Typography variant="caption" sx={{ opacity: 0.85 }}>Total</Typography>
              <Typography variant="h6" fontWeight={700}>{formatAmount(selectedSummary.total)}</Typography>
            </Paper>
          </Grid>
        </Grid>
      )}

      <Paper sx={TABLE_PAPER_SX}>
        <Box sx={{ px: 1.5, pt: 1, display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="body2" color="text.secondary">
            {selectedServicesFilter.length
              ? `Showing page ${page} • ${displayRows.length} company(s) with created orders • ${totalCount} matching company(s)`
              : 'Select at least one service to load companies with created orders'}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 110 }}>
              <InputLabel>Rows</InputLabel>
              <Select
                value={pageSize}
                label="Rows"
                onChange={(e) => setPageSize(Number(e.target.value))}
              >
                {[10, 25, 50, 100].map((size) => (
                  <MenuItem key={size} value={size}>{size}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              size="small"
              disabled={!hasPreviousPage || loading || !selectedServicesFilter.length}
              onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
              sx={{ textTransform: 'none' }}
            >
              Previous
            </Button>
            <Button
              variant="outlined"
              size="small"
              disabled={!hasNextPage || loading || !selectedServicesFilter.length}
              onClick={() => setPage((prev) => prev + 1)}
              sx={{ textTransform: 'none' }}
            >
              Next
            </Button>
          </Stack>
        </Box>
        <DataGrid
          rows={displayRows}
          columns={columns}
          loading={loading}
          autoHeight
          getRowHeight={() => 'auto'}
          checkboxSelection
          disableSelectionOnClick
          disableVirtualization
          selectionModel={selectedIds}
          onSelectionModelChange={(ids) => {
            if (ignoreNextEmptySelection.current) {
              ignoreNextEmptySelection.current = false;
              if (!ids.length) return;
            }
            setSelectedIds(ids);
          }}
          pageSize={50}
          rowsPerPageOptions={[25, 50, 100]}
          sx={{
            border: 'none',
            '& .MuiDataGrid-columnHeaders': { backgroundColor: '#f5f7fb', fontWeight: 600 },
            '& .MuiDataGrid-cell': { display: 'flex', alignItems: 'center' },
          }}
          components={{
            NoRowsOverlay: () => (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography color="text.secondary">
                  {!selectedAcademicYear
                    ? 'Select an academic year'
                    : !selectedServicesFilter.length
                      ? 'Select at least one service to load companies'
                      : 'No created orders found on this page for the selected services'}
                </Typography>
              </Box>
            ),
          }}
        />
      </Paper>
    </Box>
  );
}

export default BulkCreateIssueInvoices;
